/**
 * Retro CRT-style pass (WebGPU / TSL).
 */

import { barrelUV, colorBleeding, scanlines, vignette } from 'three/addons/tsl/display/CRT.js';
import { retroPass } from 'three/addons/tsl/display/RetroPassNode.js';
import { circle } from 'three/addons/tsl/display/Shape.js';
import { bayerDither } from 'three/addons/tsl/math/Bayer.js';
import { float, posterize, replaceDefaultUV, screenSize, uniform } from 'three/tsl';

import { type EffectContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder, type RetroEffectConfig } from '../PostProcessConfig.js';

import { WebGPUOnlyEffect } from './WebGPUOnlyEffectBase.js';

type RetroRuntimeNode = WebGPUEffectNode & {
  retroRuntime: {
    colorDepthSteps: { value: number };
    scanlineIntensity: { value: number };
    scanlineDensity: { value: number };
    scanlineSpeed: { value: number };
    vignetteIntensity: { value: number };
    bleeding: { value: number };
    curvature: { value: number };
    affineDistortion: { value: number };
  };
};

export class RetroEffect extends WebGPUOnlyEffect<RetroEffectConfig> {
  override readonly type = PostProcessPass.Retro;
  override readonly order = PostProcessRenderOrder.Retro;

  override getDefaultConfig(): RetroEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.Retro] };
  }

  override createWebGPUNode(config: RetroEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const colorDepthStepsNode = uniform(config.colorDepthSteps ?? defaults.colorDepthSteps ?? 32);
    const scanlineIntensityNode = uniform(config.scanlineIntensity ?? defaults.scanlineIntensity ?? 0.3);
    const scanlineDensityNode = uniform(config.scanlineDensity ?? defaults.scanlineDensity ?? 1);
    const scanlineSpeedNode = uniform(config.scanlineSpeed ?? defaults.scanlineSpeed ?? 0);
    const vignetteIntensityNode = uniform(config.vignetteIntensity ?? defaults.vignetteIntensity ?? 0.3);
    const bleedingNode = uniform(config.bleeding ?? defaults.bleeding ?? 0.001);
    const curvatureNode = uniform(config.curvature ?? defaults.curvature ?? 0.02);
    const affineDistortionNode = uniform(config.affineDistortion ?? defaults.affineDistortion ?? 0);
    const retroNode = retroPass(context.scene, context.camera, {
      affineDistortion: affineDistortionNode,
    });
    const effectNode: WebGPUEffectNode = {
      node: retroNode,
      compose: () => {
        const distortedUV = barrelUV(curvatureNode);
        const distortedDelta = circle(curvatureNode.add(0.1).mul(10), 1).mul(curvatureNode).mul(0.05);

        let retroPipeline: any = retroNode.getTextureNode();
        retroPipeline = replaceDefaultUV(distortedUV as any, retroPipeline as any);
        retroPipeline = colorBleeding(retroPipeline as any, bleedingNode.add(distortedDelta) as any);
        retroPipeline = bayerDither(retroPipeline as any, colorDepthStepsNode as any);
        retroPipeline = posterize(retroPipeline as any, colorDepthStepsNode as any);
        retroPipeline = vignette(retroPipeline as any, vignetteIntensityNode as any, float(0.6));
        retroPipeline = scanlines(
          retroPipeline as any,
          scanlineIntensityNode as any,
          screenSize.y.mul(scanlineDensityNode) as any,
          scanlineSpeedNode as any
        );
        return retroPipeline as any;
      },
    };
    (effectNode as RetroRuntimeNode).retroRuntime = {
      colorDepthSteps: colorDepthStepsNode,
      scanlineIntensity: scanlineIntensityNode,
      scanlineDensity: scanlineDensityNode,
      scanlineSpeed: scanlineSpeedNode,
      vignetteIntensity: vignetteIntensityNode,
      bleeding: bleedingNode,
      curvature: curvatureNode,
      affineDistortion: affineDistortionNode,
    };
    return effectNode;
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: RetroEffectConfig): void {
    const runtimeNode = node as RetroRuntimeNode;
    if (!runtimeNode.retroRuntime) return;
    const defaults = this.getDefaultConfig();
    runtimeNode.retroRuntime.colorDepthSteps.value = config.colorDepthSteps ?? defaults.colorDepthSteps ?? 32;
    runtimeNode.retroRuntime.scanlineIntensity.value = config.scanlineIntensity ?? defaults.scanlineIntensity ?? 0.3;
    runtimeNode.retroRuntime.scanlineDensity.value = config.scanlineDensity ?? defaults.scanlineDensity ?? 1;
    runtimeNode.retroRuntime.scanlineSpeed.value = config.scanlineSpeed ?? defaults.scanlineSpeed ?? 0;
    runtimeNode.retroRuntime.vignetteIntensity.value = config.vignetteIntensity ?? defaults.vignetteIntensity ?? 0.3;
    runtimeNode.retroRuntime.bleeding.value = config.bleeding ?? defaults.bleeding ?? 0.001;
    runtimeNode.retroRuntime.curvature.value = config.curvature ?? defaults.curvature ?? 0.02;
    runtimeNode.retroRuntime.affineDistortion.value = config.affineDistortion ?? defaults.affineDistortion ?? 0;
  }

  override requiresRebuild(oldConfig: RetroEffectConfig, newConfig: RetroEffectConfig): boolean {
    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.order !== newConfig.order ||
      oldConfig.type !== newConfig.type ||
      oldConfig.id !== newConfig.id
    );
  }
}
